import { useEffect, useState } from 'react';

import useRankingUsedMetrics from '../../RankingMatrixForm/useRankingUsedMetrics';
import useFilterInput from '../../../hooks/useFilterInput';
import useDateScope from '../../../hooks/useDateScope';
import relativeDateRangeTypeCheckers from '../../../contextProviders/SplashScreenProviders/UserAndAccountProviders/FiscalPeriodsProvider/relativeDateRangeTypeCheckers';
import useMetricAggregationRequest from 'hooks/useMetricAggregationRequest';

const useAggregateMetricsGroupByInput = (
  gadget: VisualisationDefinitions.RankingMatrix,
) => {
  const usedMetrics = useRankingUsedMetrics(gadget.metrics);
  const { buildMetricsAndExpressions } = useMetricAggregationRequest();
  const [metrics, setMetrics] = useState<MetricInput[] | undefined>(undefined);
  const [expressions, setExpressions] = useState<Expression2[] | undefined>(
    undefined,
  );
  const [rankings, setRankings] = useState<Queries.RankInput[] | undefined>(
    undefined,
  );
  const [groupBy, setGroupBy] = useState<Queries.GroupBy2 | undefined>();
  const { filterInput } = useFilterInput({ field: gadget.groupByField });
  const [input, setInput] = useState<
    Queries.AggregateMetricsGroupByInput | undefined
  >();
  const dateScope = useDateScope({});

  useEffect(() => {
    if (metrics && expressions && rankings && groupBy) {
      // `interval` is only set when grouping by a date field. Split it into the
      // calendar (`dateInterval`) or fiscal (`fiscalInterval`) variable — the
      // resolver expects exactly one.
      const interval = gadget.interval;
      const input: Queries.AggregateMetricsGroupByInput = {
        metrics,
        expressions,
        ranking: rankings,
        groupBy,
        filters: [filterInput],
        dateScope: [dateScope],
        dateInterval:
          interval &&
          relativeDateRangeTypeCheckers.isCalendarWithAutoInterval(interval)
            ? interval
            : undefined,
        fiscalInterval:
          interval && relativeDateRangeTypeCheckers.isFiscalInterval(interval)
            ? interval
            : undefined,
      };
      setInput(input);
    } else {
      setInput(undefined);
    }
  }, [
    dateScope,
    expressions,
    filterInput,
    gadget.interval,
    groupBy,
    metrics,
    rankings,
  ]);

  useEffect(() => {
    const {
      metrics: newMetrics,
      expressions: newExpressions,
      ready,
    } = buildMetricsAndExpressions(usedMetrics);
    // Hold off until every compound input has resolved — otherwise the request
    // would carry the expression with a partial `metrics` list, which the
    // backend rejects ("parse error: Expected EOF").
    if (!ready) {
      setMetrics(undefined);
      setExpressions(undefined);
      return;
    }
    setMetrics(newMetrics);
    setExpressions(newExpressions);
  }, [buildMetricsAndExpressions, usedMetrics]);

  useEffect(() => {
    const newRankings = [] as Queries.RankInput[];
    gadget.metrics.forEach((m) => {
      const actualMetric = usedMetrics.find((usedM) => usedM.id === m.metricId);
      const order = (() => {
        if (!actualMetric) {
          return 'asc';
        }

        const formatting = actualMetric.formatting;
        return formatting.positiveDeltaIsGood ? 'desc' : 'asc';
      })();
      if (m.isRankingEnabled) {
        newRankings.push({
          metricId: m.metricId,
          order,
        });
      }
    });

    setRankings(newRankings);
  }, [gadget.metrics, usedMetrics]);

  useEffect(() => {
    setGroupBy({
      field: gadget.groupByField,
      sortType: gadget.sorting.mode === 'alphabetical' ? 'term' : 'metric',
      sortMetric: gadget.sorting.metricId,
      sort: gadget.sorting.order,
    });
  }, [
    gadget.groupByField,
    gadget.sorting.metricId,
    gadget.sorting.mode,
    gadget.sorting.order,
  ]);

  return input;
};

export default useAggregateMetricsGroupByInput;
